// --- Input Types (Based on Shopify GraphQL Structure) ---

interface ShopifyMoney {
  amount: string;
  currencyCode: string;
}

interface ShopifyImage {
  url: string;
  width: number;
  height: number;
  altText?: string | null; // Optional alt text
}

interface ShopifyPreviewImage {
  url: string;
  width: number;
  height: number;
}

interface ShopifyMediaNode {
  __typename: string; // e.g., "Image", "Video"
  id?: string | null; // Media GID (e.g., "gid://shopify/MediaImage/123")
  alt?: string | null;
  mediaContentType: "IMAGE" | "VIDEO" | string; // Extend as needed
  position?: number | null;
  image?: ShopifyImage | null; // Present if mediaContentType is IMAGE
  preview?: {
    image?: ShopifyPreviewImage | null;
  } | null;
  // Add fields for other media types like Video if necessary
  // sources?: { url: string; format: string; mimeType: string }[];
}

interface ShopifyMediaEdge {
  node: ShopifyMediaNode;
}

interface ShopifySelectedOption {
  name: string;
  value: string;
}

interface ShopifyVariantNode {
  legacyResourceId: string; // Numeric ID as a string
  sku?: string | null;
  availableForSale: boolean;
  price: ShopifyMoney;
  compareAtPrice?: ShopifyMoney | null;
  selectedOptions: ShopifySelectedOption[];
  // Add other variant fields if needed (e.g., image, weight)
}

interface ShopifyVariantEdge {
  node: ShopifyVariantNode;
}

interface ShopifyPriceRange {
  minVariantPrice: ShopifyMoney;
  maxVariantPrice: ShopifyMoney;
}

interface ShopifyOption {
  id: string;
  name: string;
  values: string[];
}

export interface ShopifyProductData {
  id: string; // Numeric ID as a string
  handle: string;
  title: string;
  descriptionHtml: string;
  vendor: string;
  createdAt: string; // ISO 8601 date string
  updatedAt: string; // ISO 8601 date string
  featuredImage?: ShopifyImage | null;
  priceRange: ShopifyPriceRange; // Assuming this is always present based on usage
  compareAtPriceRange?: ShopifyPriceRange | null; // Optional compare at price
  options: ShopifyOption[];
  variants: {
    edges: ShopifyVariantEdge[];
  };
  media: {
    edges: ShopifyMediaEdge[];
  };
  // Add other product fields if needed (e.g., productType, tags)
}

// --- Output Types (Target JSON Structure) ---

interface TargetPrice {
  amount: number;
  currencyCode: string;
}

interface TargetPriceRange {
  minVariantPrice: TargetPrice;
  maxVariantPrice: TargetPrice;
}

interface TargetMediaPreviewImage {
  aspect_ratio: number;
  height: number;
  width: number;
  src: string; // Mapped from url
}

interface TargetMedia {
  alt: string | null;
  id: number; // Numeric ID parsed from GID
  position: number;
  preview_image: TargetMediaPreviewImage;
  aspect_ratio: number;
  height: number;
  media_type: string; // Lowercase (e.g., "image", "video")
  src: string; // Mapped from url
  width: number;
}

interface TargetImage {
  url: string;
  width: number;
  height: number;
  altText: string | null;
}

interface TargetVariant {
  id: string; // Using legacy ID string directly
  sku: string;
  availableForSale: boolean;
  image: Record<string, never>; // Empty object as requested
  price: number; // Parsed float
  selectedOptions: ShopifySelectedOption[];
  // compareAtPrice?: number | null; // Optional: Add if needed
}

interface TargetProductJson {
  id: string; // Using handle as ID
  shopifyId: number; // Parsed legacy ID
  title: string;
  description: string; // HTML description
  handle: string;
  vendor: string;
  image: string | null; // URL of featured image
  featuredImage: TargetImage | null;
  images: TargetImage[]; // Derived list of images
  media: TargetMedia[]; // Detailed media list
  variants: TargetVariant[];
  options: ShopifyOption[]; // Direct mapping
  skus: string[];
  priceRange: TargetPriceRange;
  compareAtPriceRange: TargetPriceRange; // Uses priceRange as fallback
  createdAt: string;
  updatedAt: string;
  generatedTags: boolean; // Example default value
}

// --- Transformer Function ---

/**
 * Transforms Shopify product data from a GraphQL query result
 * into a specific target JSON structure.
 *
 * @param productData - The raw product data from Shopify's GraphQL API.
 * @returns The transformed product data in the TargetProductJson format.
 */
export function transformShopifyProduct(
  productData: ShopifyProductData,
): TargetProductJson {
  // --- Variants Transformation ---
  const variants: TargetVariant[] = productData.variants.edges.map(
    ({ node: v }) => ({
      id: v.id.split("/").at(-1), // Use legacy ID string
      sku: v.sku ?? "", // Handle potential null SKU
      availableForSale: v.availableForSale,
      image: {}, // Empty object as per target structure - unusual, but kept
      price: parseFloat(v.price.amount), // Convert amount string to number
      selectedOptions: v.selectedOptions,
      // You might want to add compareAtPrice here if needed:
      // compareAtPrice: v.compareAtPrice ? parseFloat(v.compareAtPrice.amount) : null,
    }),
  );

  // Extract non-empty SKUs
  const skus: string[] = variants
    .map((v) => v.sku)
    .filter((sku): sku is string => sku !== ""); // Type guard ensures string[]

  // --- Media Transformation ---

  // Helper to process a single media node
  const getMediaTypeAndDetails = (
    node: ShopifyMediaNode,
  ): {
    mediaType: string;
    url: string | null;
    width: number | null;
    height: number | null;
    previewImage: TargetMediaPreviewImage | null;
    position: number;
    gid: string | null; // Keep original GID string
  } => {
    let mediaType: string = node.mediaContentType; // Start with original type
    let url: string | null = null;
    let width: number | null = null;
    let height: number | null = null;
    let previewImage: TargetMediaPreviewImage | null = null;
    // Use position from node, default to 1 if null/undefined
    const position = node.position ?? 1;
    const gid = node.id ?? null; // Media GID

    if (node.mediaContentType === "IMAGE" && node.image) {
      mediaType = "image"; // Map to lowercase 'image' like target
      url = node.image.url;
      width = node.image.width;
      height = node.image.height;
      // Process preview image if available
      if (node.preview?.image) {
        const pw = node.preview.image.width;
        const ph = node.preview.image.height;
        previewImage = {
          // Calculate aspect ratio safely, default to 1 if dimensions are zero
          aspect_ratio: pw && ph ? parseFloat((pw / ph).toFixed(3)) : 1,
          height: ph,
          width: pw,
          src: node.preview.image.url, // Map url to src
        };
      }
    }
    // Add handling for VIDEO or other types if needed
    // else if (node.mediaContentType === 'VIDEO' && node.sources) {
    //   mediaType = "video";
    //   // Potentially pick the first source URL or handle differently
    //   url = node.sources[0]?.url ?? null;
    //   // Video dimensions might come from elsewhere or need assumptions
    //   width = node.image?.width ?? null; // Use poster image dimensions?
    //   height = node.image?.height ?? null;
    //   // Video preview might be different
    //   if (node.preview?.image) { ... }
    // }

    return { mediaType, url, width, height, previewImage, position, gid };
  };

  const mediaItems: TargetMedia[] = productData.media.edges
    .map(({ node }, index): TargetMedia | null => {
      const { mediaType, url, width, height, previewImage, position, gid } =
        getMediaTypeAndDetails(node);

      if (!url || !width || !height || !previewImage || !gid) {
        return null; // Indicate this item should be skipped
      }

      let numericId: number = 0; // Default ID if parsing fails
      try {
        const idPart = gid.substring(gid.lastIndexOf("/") + 1);
        const parsedId = parseInt(idPart, 10);
        if (!isNaN(parsedId)) {
          numericId = parsedId;
        } else {
        }
      } catch (error) {}

      return {
        alt: node.alt ?? null, // Use nullish coalescing for alt text
        id: numericId, // Use parsed numeric ID or 0/default
        position: position ?? index + 1, // Use node's position or index+1 as fallback
        preview_image: previewImage, // Already checked for null above
        aspect_ratio: previewImage.aspect_ratio, // Use preview aspect ratio
        height: height, // Already checked for null above
        media_type: mediaType, // Lowercase media type
        src: url, // Map url to src, already checked for null
        width: width, // Already checked for null above
      };
    })
    .filter((item): item is TargetMedia => item !== null); // Type guard filters out nulls

  // --- Images Derivation ---
  // Create the simpler 'images' array from the processed 'mediaItems'
  const images: TargetImage[] = mediaItems
    .filter((m) => m.media_type === "image") // Only include items marked as 'image'
    .map((m) => ({
      url: m.src, // Use 'src' which we mapped from the main image url
      width: m.width,
      height: m.height,
      altText: m.alt, // Use the alt text from the media item
    }));

  // --- Price Formatting ---

  // Helper to convert ShopifyMoney to TargetPrice, handling potential errors
  const formatPrice = (money: ShopifyMoney | null | undefined): TargetPrice => {
    // Provide default if input is null/undefined
    if (!money) {
      return { amount: 0, currencyCode: "USD" }; // Default to 0 USD
    }
    let amount = 0;
    try {
      amount = parseFloat(money.amount);
      if (isNaN(amount)) {
        amount = 0;
      }
    } catch (error) {
      amount = 0; // Default to 0 on error
    }
    return {
      amount: amount,
      currencyCode: money.currencyCode ?? "USD", // Default currency if missing
    };
  };

  // Safely access price range, providing defaults if needed
  const priceRange: TargetPriceRange = productData.priceRange
    ? {
        minVariantPrice: formatPrice(productData.priceRange.minVariantPrice),
        maxVariantPrice: formatPrice(productData.priceRange.maxVariantPrice),
      }
    : {
        // Provide a default structure if priceRange is unexpectedly null/undefined
        minVariantPrice: { amount: 0, currencyCode: "USD" }, // Use a sensible default
        maxVariantPrice: { amount: 0, currencyCode: "USD" },
      };

  // Safely access compareAtPrice range
  // Use priceRange as fallback if compareAtPriceRange is null/missing or invalid
  const compareAtPriceRange: TargetPriceRange = productData.compareAtPriceRange
    ? {
        minVariantPrice: formatPrice(
          productData.compareAtPriceRange.minVariantPrice,
        ),
        maxVariantPrice: formatPrice(
          productData.compareAtPriceRange.maxVariantPrice,
        ),
      }
    : priceRange; // Fallback to regular price range if compareAt is not set

  return {
    id: productData.handle, // Use handle for the top-level ID
    shopifyId: productData.id.split("/").at(-1), // Use safely parsed legacy ID
    title: productData.title,
    description: productData.descriptionHtml, // Use HTML description
    handle: productData.handle,
    vendor: productData.vendor,
    image: productData.featuredImage?.url ?? null, // URL of featured image (optional chaining)
    featuredImage: productData.featuredImage
      ? {
          // Map featured image details if it exists
          url: productData.featuredImage.url,
          width: productData.featuredImage.width,
          height: productData.featuredImage.height,
          altText: productData.featuredImage.altText ?? null, // Handle optional alt text
        }
      : null, // Otherwise, null
    images: images, // The derived list of images
    media: mediaItems, // The detailed media list
    variants: variants,
    options: productData.options, // Direct mapping
    skus: skus,
    priceRange: priceRange,
    compareAtPriceRange: compareAtPriceRange,
    createdAt: productData.createdAt,
    updatedAt: productData.updatedAt,
    generatedTags: false, // Set default as per your example
  };
}
